import { useCallback } from 'react';
import { NotifiableError } from '@bugsnag/js';
import { bugtracker } from '@99designs/design-services-common';
import { FulfilmentStrategy, KeyValue } from '@99designs/graph-utils/types';
import { __url } from '@99designs/i18n';
import { BRIEF_PAGE_NAME, sendAddToCartTrackingEvent } from '@99designs/tracking';
import {
    useCreateRequestWithEventMutation,
    useDesignLiveAppointmentUpdatedMutation,
    useRequestLazyQuery,
    useSubmitRequestMutation,
} from '../../graph';
import { ClaimBriefMutation } from '../../graph/ClaimBrief.generated';
import { OrphanedBriefError } from '../../lib/errors';
import { UpsertToCartRequest, useCart } from '../../lib/hooks';
import { BriefRequestFragment } from '../BriefForm/brief.generated';
import { CARE_MPVID, DesignServiceRequestType, MERCHANT_SERVICE_CONFIG } from './consts';
import { getWorkIdFromDynamicFields } from './getWorkIdFromDynamicFields';
import { designLiveDataFromBrief, getRequestType } from './requestDataFromBrief';

/*
 *  Note: Experiments are used in `requests` to direct traffic between the new and old stacks
 *  Remove experiments when revision requests are no longer going through MCP,
 *  as there will be no need to send traffic to the old stack anymore
 */

const experiments = JSON.stringify([
    'useRequests',
    // TODO: These experiments are no longer used in Requests anymore so they should be deleted.
    'useCollaborate',
    'useCollaborateForDesignLive',
]);

export const useSubmitToRequests = (
    locale: string,
    accessToken: string,
    shopperId: string,
    briefId: string
) => {
    const [requestQuery] = useRequestLazyQuery();
    const [designLiveAppointmentUpdated] = useDesignLiveAppointmentUpdatedMutation();
    const [createRequestWithEvent] = useCreateRequestWithEventMutation();
    const [submitRequest] = useSubmitRequestMutation();
    const { upsertToCart, requestIdByBriefId: requestIdByBriefIdFromCart } = useCart(locale, {
        accessToken,
        shopperId,
    });

    return useCallback(
        async (data: ClaimBriefMutation) => {
            try {
                const productData = formatProductData(data);

                let expertRequestID = await requestIdByBriefIdFromCart(briefId);
                const hasRequestForBriefInCart = expertRequestID.length > 0;

                if (data.claimStructuredBrief.request) {
                    return await rnbFlow(
                        hasRequestForBriefInCart,
                        submitRequest,
                        designLiveAppointmentUpdated,
                        data.claimStructuredBrief.request,
                        productData,
                        briefId,
                        data,
                        upsertToCart
                    );
                } else {
                    bugtracker.notify(
                        new OrphanedBriefError(
                            `Encountered orphaned brief in useSubmitToRequests hook: ${briefId}`
                        )
                    );
                }

                // todo: delete everything past this point once RnB is fully rolled out

                if (hasRequestForBriefInCart && productData.experience === 'DesignLive') {
                    const { data } = await requestQuery({
                        variables: {
                            id: expertRequestID,
                        },
                    });
                    if (!data) throw new Error(`Failed to call get request`);
                    const request = data.request;

                    if (
                        request.facts?.designLiveAppointmentTime !==
                        productData.briefDesignLiveData.designLiveAppointmentTime
                    ) {
                        const { errors } = await designLiveAppointmentUpdated({
                            variables: {
                                requestId: expertRequestID,
                                input: {
                                    timezone: productData.briefDesignLiveData.timezone!,
                                    appointmentTime:
                                        productData.briefDesignLiveData.designLiveAppointmentTime!,
                                },
                            },
                        });

                        if (errors && errors.length > 0) {
                            throw errors;
                        }
                    }
                }

                if (!hasRequestForBriefInCart) {
                    const purchaseIntentionInitiatedEvent = {
                        withDesignLive: productData.experience === 'DesignLive',
                        ...productData.briefDesignLiveData,
                    };

                    const resp = await createRequestWithEvent({
                        variables: {
                            input: {
                                facts: {
                                    locale,
                                },
                                purchaseIntentionInitiatedEvent,
                            },
                        },
                    });

                    if (!resp.data) throw new Error(`Failed to call create new request`);

                    expertRequestID = resp.data?.createRequestWithEvent?.publicId;

                    sendAddToCartTrackingEvent({
                        pageName: BRIEF_PAGE_NAME,
                        pageStage: 'Design',
                        pageSection: 'Design Services',
                        product_id: productData.expertProductPrd, // should be mpvId
                        name: productData.productName,
                        core_product_id: productData.expertProductPrd,
                        price: productData.productPriceInDecimal,
                        currency: data.claimStructuredBrief.product?.pricing.amount.currency,
                    });
                }

                await submitToCart(productData, briefId, expertRequestID, data, upsertToCart);

                return;
            } catch (e) {
                bugtracker.notify(e as NotifiableError, (event) => {
                    event.context = `Error during submission to requests for: ${briefId}.`;
                });
                throw e;
            }
        },
        [
            requestIdByBriefIdFromCart,
            briefId,
            upsertToCart,
            requestQuery,
            designLiveAppointmentUpdated,
            createRequestWithEvent,
            locale,
            submitRequest,
        ]
    );
};

export async function rnbFlow(
    hasRequestForBriefInCart: boolean,
    submitRequest: ReturnType<typeof useSubmitRequestMutation>[0],
    designLiveAppointmentUpdated: ReturnType<typeof useDesignLiveAppointmentUpdatedMutation>[0],
    request: BriefRequestFragment,
    productData: ReturnType<typeof formatProductData>,
    briefId: string,
    data: ClaimBriefMutation,
    upsertToCart: (payload: UpsertToCartRequest) => Promise<void>
) {
    if (shouldUpdateDesignLiveAppointment(productData, request)) {
        await updateDesignLiveAppointment(productData, designLiveAppointmentUpdated, request);
    }
    if (!hasRequestForBriefInCart) {
        await submitRequestAndTrack(submitRequest, request, productData);
    }
    await submitToCart(productData, briefId, request.id, data, upsertToCart);
    return;
}

export function shouldUpdateDesignLiveAppointment(
    productData: ReturnType<typeof formatProductData>,
    request: BriefRequestFragment
) {
    return (
        productData.experience === 'DesignLive' &&
        request.facts?.designLiveAppointmentTime !==
            productData.briefDesignLiveData.designLiveAppointmentTime
    );
}

export async function updateDesignLiveAppointment(
    productData: ReturnType<typeof formatProductData>,
    designLiveAppointmentUpdated: ReturnType<typeof useDesignLiveAppointmentUpdatedMutation>[0],
    request: BriefRequestFragment
) {
    if (
        !productData.briefDesignLiveData.timezone ||
        !productData.briefDesignLiveData.designLiveAppointmentTime
    ) {
        throw new Error(
            `design live data is not sufficient to book an appointment: ${productData.briefDesignLiveData}`
        );
    }
    const { errors } = await designLiveAppointmentUpdated({
        variables: {
            requestId: request.id,
            input: {
                timezone: productData.briefDesignLiveData.timezone,
                appointmentTime: productData.briefDesignLiveData.designLiveAppointmentTime,
            },
        },
    });

    if (errors && errors.length > 0) {
        throw errors;
    }
}

export async function submitRequestAndTrack(
    submitRequest: ReturnType<typeof useSubmitRequestMutation>[0],
    request: BriefRequestFragment,
    productData: ReturnType<typeof formatProductData>
) {
    const resp = await submitRequest({
        variables: {
            requestId: request.id,
        },
    });
    if (!resp.data) throw new Error(`Failed to call submit request`);

    sendAddToCartTrackingEvent({
        pageName: BRIEF_PAGE_NAME,
        pageStage: 'Design',
        pageSection: 'Design Services',
        product_id: productData.expertProductPrd, // should be mpvId
        name: productData.productName,
        core_product_id: productData.expertProductPrd,
        price: productData.productPriceInDecimal,
        currency: request.expertService?.price.currency,
    });
}

export function getCartProductData(
    productData: ReturnType<typeof formatProductData>,
    briefId: string,
    expertRequestID: string,
    data: ClaimBriefMutation
) {
    const cartProduct: UpsertToCartRequest['product'] = {
        productKey: productData.expertProductPrd,
        productVersion: productData.cimpressProductVersion,
        productName: productData.productName,
        fulfilmentData: {
            expertRequestID,
            experiments,
            // The following fields are required in the order confirmation UX mod:
            productBriefID: briefId,
            postPaymentUrl: __url('/graphic-design/dashboard'),
            printProductMPVId: data.claimStructuredBrief.fulfilment?.mpvId || '',
            fulfilmentStrategy: data.claimStructuredBrief.fulfilmentStrategy ?? undefined,
            ...(productData.experience
                ? { withDesignLive: productData.experience === 'DesignLive' }
                : {}),
        },
    };

    // TODO: Disgusting. But necessary for now.
    const fulfilmentSize = data.claimStructuredBrief.fulfilment?.size;
    const expertService = data.claimStructuredBrief.request?.expertService;
    // Include fulfilment size only for legacy services (caredesign, designlive)
    const isLegacyService = expertService?.isLegacy ?? true;
    const shouldIncludeFulfilmentSize = fulfilmentSize && isLegacyService;

    if (shouldIncludeFulfilmentSize) {
        cartProduct.selectedAttributes = [
            {
                value: fulfilmentSize,
                key: 'ProjectSize',
            },
        ];
    }

    const serviceProductOptions = sanitizeServiceProductOptions(
        data.claimStructuredBrief.request?.facts?.serviceProductOptions
    );

    if (serviceProductOptions.length > 0) {
        const selectedAttributes = cartProduct.selectedAttributes ?? [];
        cartProduct.selectedAttributes = [...selectedAttributes, ...serviceProductOptions];
    }

    return cartProduct;
}

function sanitizeServiceProductOptions(
    options: KeyValue[] | null | undefined
): { key: string; value: string }[] {
    if (!options?.length) {
        return [];
    }

    return options.map(({ key, value }) => ({ key, value }));
}

async function submitToCart(
    productData: ReturnType<typeof formatProductData>,
    briefId: string,
    expertRequestID: string,
    data: ClaimBriefMutation,
    upsertToCart: (payload: UpsertToCartRequest) => Promise<void>
) {
    const cartProduct = getCartProductData(productData, briefId, expertRequestID, data);

    // NOTE: Can't use requestID as the correlationID until the brief has a requestID on it (or it's the same ID))
    await upsertToCart({
        product: cartProduct,
        correlationId: briefId,
        workId: getWorkIdFromDynamicFields(data.claimStructuredBrief.dynamicFields),
    });
}

export function formatProductData(data: ClaimBriefMutation) {
    const product = data.claimStructuredBrief.product;
    const expertService = data.claimStructuredBrief.request?.expertService;
    const fulfillmentStrategy =
        data.claimStructuredBrief.fulfilmentStrategy ?? product?.fulfilmentStrategy;
    if (!fulfillmentStrategy) {
        throw new Error('Fulfillment strategy is not present in claim structured brief response');
    }
    const productName = expertService?.title ?? product?.title;
    if (!productName) {
        throw new Error('Product name is not present in claim structured brief response');
    }
    const productPriceInDecimal =
        expertService?.price?.listPrice.taxed ??
        Math.round((product?.pricing.amount.amountInCents ?? 0) / 100);
    if (!productPriceInDecimal) {
        throw new Error('Product price is not present in claim structured brief response');
    }
    const cimpressProductKey =
        expertService?.serviceProduct.productKey ??
        product?.cimpressProductConfig?.cimpressProductKey;
    if (!cimpressProductKey) {
        throw new Error('Cimpress product key is not present in claim structured brief response');
    }
    const cimpressProductVersion =
        expertService?.serviceProduct.pimMerchantVersion ??
        product?.cimpressProductConfig?.cimpressProductVersion;
    if (!cimpressProductVersion) {
        throw new Error(
            'Cimpress product version is not present in claim structured brief response'
        );
    }

    const requestType = getRequestType(data.claimStructuredBrief.dynamicFields);

    const careCollabExperience = getCareCollabExperience(
        fulfillmentStrategy,
        requestType,
        expertService,
        cimpressProductKey
    );

    const expertProductPrd = careCollabExperience
        ? carePrdForCart(careCollabExperience)
        : cimpressProductKey;

    const briefDesignLiveData = designLiveDataFromBrief(data.claimStructuredBrief.dynamicFields);

    return {
        fulfillmentStrategy,
        productName,
        productPriceInDecimal,
        cimpressProductKey,
        cimpressProductVersion,
        experience: careCollabExperience,
        expertProductPrd,
        briefDesignLiveData,
    };
}

function getCareCollabExperience(
    fulfillmentStrategy: FulfilmentStrategy,
    requestType: DesignServiceRequestType | false,
    expertService: BriefRequestFragment['expertService'] | undefined | null,
    cimpressProductKey: string
): DesignServiceRequestType | false {
    if (fulfillmentStrategy !== 'FULFILMENT_STRATEGY_CARE' || !requestType) {
        return false;
    }

    const isCareProduct = expertService
        ? isCareExpertService(expertService)
        : isCareProductKey(cimpressProductKey);

    return isCareProduct ? requestType : false;
}

function isCareExpertService(expertService: BriefRequestFragment['expertService']): boolean {
    if (!expertService) {
        return false;
    }

    const mpvId = expertService.serviceProduct.mpvId.toLowerCase();
    return mpvId === CARE_MPVID.Standard || mpvId === CARE_MPVID.DesignLive;
}

function isCareProductKey(productKey: string): boolean {
    return (
        productKey === MERCHANT_SERVICE_CONFIG.Standard ||
        productKey === MERCHANT_SERVICE_CONFIG.DesignLive
    );
}

// TODO: Get the CARE PRD from the parent request!
// Note: this function accounts for the fact that there is one expert product for two PRDs for CARE products
function carePrdForCart(careCollaborationExperience: DesignServiceRequestType): string {
    return MERCHANT_SERVICE_CONFIG[careCollaborationExperience];
}
